import React, { useState, useCallback } from 'react'
import { Box, Typography, Button, useTheme, useMediaQuery, Stepper, Step, StepLabel, MobileStepper } from '@mui/material'
import { GLOBAL_PATHS } from '@/lib/globals'
import { motion, AnimatePresence } from 'framer-motion'
import { useSwipeable } from 'react-swipeable'
import { useTranslation } from '@/app/i18n/client'
import { defaultNS, fallbackLng } from '@/app/i18n/settings'

const slides = [
  {
    image: GLOBAL_PATHS.GLASS2 + '4644649.webp',
    slideKey: 'slide1',
    colors: ['rgba(15, 31, 58, 1)', 'rgba(26, 45, 79, 1)'],
    circleColors: ['#1e3c72', '#2a5298'],
  },
  {
    image: GLOBAL_PATHS.GLASS2 + '4644639.webp',
    slideKey: 'slide2',
    colors: ['rgba(74, 21, 99, 1)', 'rgba(45, 0, 51, 1)'],
    circleColors: ['#8e2de2', '#aa07bd'],
  },
  {
    image: GLOBAL_PATHS.GLASS2 + '4644637.webp',
    slideKey: 'slide3',
    colors: ['rgba(10, 77, 69, 1)', 'rgba(26, 95, 71, 1)'],
    circleColors: ['#11998e', '#38ef7d'],
  },
]

const slideVariants = {
  initial: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
  animate: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, type: 'spring' as const },
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.3, type: 'spring' as const },
  }),
}

interface WelcomeOnboardingProps {
  onClose: () => void
}

export default function OnboardingWrapper({ onClose }: WelcomeOnboardingProps) {
  const [index, setIndex] = useState(0)
  const [direction, setDirection] = useState(0)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { t } = useTranslation(fallbackLng, defaultNS, {})

  const handleNext = useCallback(() => {
    if (index < slides.length - 1) {
      setDirection(1)
      setIndex(prev => prev + 1)
    }
  }, [index])

  const handlePrev = useCallback(() => {
    if (index > 0) {
      setDirection(-1)
      setIndex(prev => prev - 1)
    }
  }, [index])

  const swipeHandlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    trackMouse: false,
    preventScrollOnSwipe: true,
    delta: 30,
  })

  const currentSlide = slides[index]
  const isLastSlide = index === slides.length - 1

  return (
    <Box
      suppressHydrationWarning
      sx={{
        p: 0,
        m: 0,
        top: 0,
        left: 0,
        zIndex: 2000,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        position: 'fixed',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: "rgba(17, 25, 40, .35)",
        backdropFilter: "blur(10px) saturate(181%)",
      }}
    >
      <Box
        sx={{
          p: 0,
          boxShadow: 8,
          maxWidth: 500,
          display: 'flex',
          overflow: 'hidden',
          alignItems: 'center',
          position: 'relative',
          flexDirection: 'column',
          borderRadius: isMobile ? 0 : 5,
          width: isMobile ? '100vw' : 400,
          height: isMobile ? '100vh' : 'auto',
          background: `linear-gradient(135deg, ${currentSlide.colors[0]} 0%, ${currentSlide.colors[1]} 100%)`,
        }}
      >
        <Box
          sx={{
            zIndex: 1,
            left: '50%',
            display: 'flex',
            borderRadius: '50%',
            position: 'absolute',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? 280 : 320,
            height: isMobile ? 280 : 320,
            top: isMobile ? '15%' : '10%',
            transform: 'translateX(-50%)',
            background: `${currentSlide.circleColors[0]}30`,
          }}
        >
          <Box
            sx={{
              zIndex: 2,
              display: 'flex',
              borderRadius: '50%',
              alignItems: 'center',
              justifyContent: 'center',
              width: isMobile ? 200 : 220,
              height: isMobile ? 200 : 220,
              background: `${currentSlide.circleColors[1]}90`,
            }}
          />
        </Box>

        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={index}
            custom={direction}
            variants={{
              initial: (dir: number) => slideVariants.initial(dir),
              animate: slideVariants.animate,
              exit: (dir: number) => slideVariants.exit(dir),
            }}
            initial="initial"
            animate="animate"
            exit="exit"
            {...(isMobile ? swipeHandlers : {})}
            style={{
              zIndex: 2,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              justifyContent: 'center',
              padding: isMobile ? 24 : 32,
              paddingBottom: isMobile ? 110 : 96,
              minHeight: isMobile ? '100vh' : undefined,
            }}
          >
            <img
              loading='eager'
              fetchPriority='high'
              alt={t(`onboarding.${currentSlide.slideKey}.title`)}
              src={currentSlide.image}
              style={{
                zIndex: 3,
                left: '50%',
                position: 'absolute',
                objectFit: 'contain',
                width: isMobile ? 120 : 140,
                height: isMobile ? 120 : 140,
                transform: 'translateX(-50%)',
                top: isMobile ? '24%' : '24%',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
              }}
            />
            <Box sx={{ mt: isMobile ? 42 : 45 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                align="center"
                gutterBottom
                color="#fff"
                sx={{ zIndex: 2 }}
              >
                {t(`onboarding.${currentSlide.slideKey}.title`)}
              </Typography>
              <Typography
                variant="body1"
                align="center"
                color="rgba(255,255,255,0.85)"
                mb={3}
                sx={{ zIndex: 2, mx: 'auto' }}
              >
                {t(`onboarding.${currentSlide.slideKey}.desc`)}
              </Typography>
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <Box
          width="100%"
          px={3}
          pb={2}
          sx={{
            pt: 2,
            left: 0,
            zIndex: 2,
            bottom: 0,
            position: 'absolute',
            bgcolor: !isMobile ? 'rgba(10,10,30,0.85)' : 'transparent',
          }}
        >
          {isMobile ? (
            <>
              <MobileStepper
                nextButton={null}
                backButton={null}
                position="static"
                activeStep={index}
                steps={slides.length}
                sx={{
                  bgcolor: 'transparent',
                  justifyContent: 'center',
                  mb: 1,
                  '& .MuiMobileStepper-dot': {
                    backgroundColor: 'rgba(255,255,255,0.4)',
                  },
                  '& .MuiMobileStepper-dotActive': {
                    backgroundColor: '#000',
                    border: '2px solid #fff',
                  }
                }}
              />
              <Box display="flex" justifyContent="space-between" width="100%" pb={1}>
                <Button
                  onClick={handlePrev}
                  disabled={index === 0}
                  sx={{ color: '#fff', opacity: index === 0 ? 0.5 : 1 }}
                >
                  {t('onboarding.navigation.previous')}
                </Button>
                <Button
                  disableElevation
                  variant="contained"
                  onClick={isLastSlide ? onClose : handleNext}
                  sx={{
                    background: `${theme.palette.common.black} !important`,
                    minWidth: 100
                  }}
                >
                  {isLastSlide
                    ? t('onboarding.navigation.start')
                    : t('onboarding.navigation.next')
                  }
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Stepper activeStep={index} alternativeLabel sx={{ bgcolor: 'transparent', pb: 1 }}>
                {slides.map((_, i) => (
                  <Step key={i}>
                    <StepLabel sx={{
                      '& .MuiStepIcon-root': {
                        color: i === index ? `${currentSlide.circleColors[1]}90` : '#000',
                        backgroundColor: i === index ? '#000' : 'transparent',
                        borderRadius: '50%',
                      },
                      '& .MuiSvgIcon-root.Mui-completed': {
                        color: `${currentSlide.circleColors[1]}90`,
                        backgroundColor: `#ffffff`,
                        borderRadius: '50%',
                      },
                      '& .MuiSvgIcon-root.Mui-active': {
                        color: `${currentSlide.circleColors[1]}90`,
                      },
                      '& .MuiStepIcon-text': {
                        fill: '#fff',
                      },
                    }} />
                  </Step>
                ))}
              </Stepper>
              <Box display="flex" justifyContent="space-between" width="100%" pb={1}>
                <Button
                  onClick={handlePrev}
                  disabled={index === 0}
                  sx={{ color: '#fff', opacity: index === 0 ? 0.5 : 1 }}
                >
                  {t('onboarding.navigation.previous')}
                </Button>
                <Button
                  disableElevation
                  variant="contained"
                  onClick={isLastSlide ? onClose : handleNext}
                  sx={{
                    background: `${theme.palette.common.black} !important`,
                    minWidth: 100
                  }}
                >
                  {isLastSlide
                    ? t('onboarding.navigation.start')
                    : t('onboarding.navigation.next')
                  }
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}